import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { images } from '../../assets';
import { homeAnchorItems } from '../../navigation/navItems';
import { useSmoothScroll } from '../../motion/SmoothScroll';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollTo, lenis } = useSmoothScroll() || {};
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => {
      const y = lenis?.scroll ?? window.scrollY;
      setScrolled(y > 24);
    };
    onScroll();
    if (lenis) {
      lenis.on('scroll', onScroll);
      return () => lenis.off('scroll', onScroll);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [lenis]);

  const updateActiveSection = useCallback(() => {
    if (!isHomePage) return;

    const scrollY = lenis?.scroll ?? window.scrollY;
    const scrollPosition = scrollY + Math.max(window.innerHeight * 0.35, 120);
    const nearBottom =
      window.innerHeight + scrollY >= document.documentElement.scrollHeight - 80;

    if (nearBottom) {
      setActiveSection(homeAnchorItems[homeAnchorItems.length - 1].id);
      return;
    }

    let current = homeAnchorItems[0].id;
    homeAnchorItems.forEach((item) => {
      const section = document.getElementById(item.id);
      if (section && section.offsetTop <= scrollPosition) {
        current = item.id;
      }
    });
    setActiveSection(current);
  }, [isHomePage, lenis]);

  useEffect(() => {
    updateActiveSection();
    if (lenis) {
      lenis.on('scroll', updateActiveSection);
      return () => lenis.off('scroll', updateActiveSection);
    }
    window.addEventListener('scroll', updateActiveSection, { passive: true });
    return () => window.removeEventListener('scroll', updateActiveSection);
  }, [lenis, updateActiveSection]);

  useEffect(() => {
    if (!isHomePage) setActiveSection('home');
  }, [isHomePage]);

  useEffect(() => {
    if (!isHomePage || !location.hash) return;
    const id = location.hash.replace('#', '');
    const timer = setTimeout(() => {
      if (scrollTo) scrollTo(`#${id}`);
    }, 80);
    return () => clearTimeout(timer);
  }, [isHomePage, location.hash, scrollTo]);

  const handleAnchorClick = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    if (scrollTo) scrollTo(href);
    else document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleOffHomeAnchor = (e, id) => {
    e.preventDefault();
    setMenuOpen(false);
    navigate(`/#${id}`);
  };

  const goContact = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHomePage) {
      if (scrollTo) scrollTo('#contact');
      else document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#contact');
    }
  };

  const primaryLinks = homeAnchorItems.filter((item) =>
    ['services', 'about', 'work'].includes(item.id)
  );

  return (
    <header
      className={`studio-nav ${menuOpen ? 'is-open' : ''} ${scrolled ? 'is-scrolled' : ''}`}
    >
      <Link
        to="/"
        className="studio-nav__brand"
        onClick={() => setMenuOpen(false)}
        aria-label="TheStudioVS home"
      >
        <span className="studio-nav__mark">
          <img src={images.logo} alt="" />
        </span>
        <span className="studio-nav__wordmark">
          TheStudio<span>VS</span>
        </span>
      </Link>

      <nav className="studio-nav__links" aria-label="Primary">
        <div className="studio-nav__cluster">
          {primaryLinks.map((item) =>
            isHomePage ? (
              <a
                key={item.id}
                href={item.href}
                className={activeSection === item.id ? 'is-active' : ''}
                onClick={(e) => handleAnchorClick(e, item.href)}
              >
                {item.label}
              </a>
            ) : (
              <a
                key={item.id}
                href={`/#${item.id}`}
                onClick={(e) => handleOffHomeAnchor(e, item.id)}
              >
                {item.label}
              </a>
            )
          )}
          <Link
            to="/portfolio"
            className={location.pathname.startsWith('/portfolio') ? 'is-active' : ''}
            onClick={() => setMenuOpen(false)}
          >
            Portfolio
          </Link>
        </div>
        <a href="#contact" className="studio-nav__cta" onClick={goContact}>
          Get in touch
        </a>
      </nav>

      <motion.button
        type="button"
        className={`studio-nav__burger ${menuOpen ? 'is-active' : ''}`}
        onClick={() => setMenuOpen((o) => !o)}
        aria-label="Toggle navigation menu"
        whileTap={{ scale: 0.94 }}
      >
        <span />
        <span />
        <span />
      </motion.button>
    </header>
  );
};

export default Header;
